import { For, If, location, reactive, showDialog, watch } from 'cdui-js';
import { addAccState, lastProgress, nextProgress } from './store';
import { DiFiCheckBox } from '../../../../components/DiFi/DiFiCheckBox';
import { DiFiLoading } from '../../../../components/DiFi/DiFiLoading/index.tsx';
import { initBusConfigs, busConfigs, busConfigsMaps, busConfigsTick } from '../../../../stores/bus-config';
import { fetchAllInfos, saveApply } from '../../../../api/open-account';
import {
  fetchFinishedSecuritiesAccountTypes,
  fetchProfessionalInvestorAssessmentStatus,
  type ProfessionalInvestorAssessmentStatus,
} from '../../../../api/account';
import { fetchVirtualAssetRiskLatest } from '../../../../api/my';
import { postToApp } from '../../../../lib/rnBridge';
import { showToast } from '../../components/Toast';
import {
  hydrateVirtualApplyFromApplyData,
  notifyAppContinueAddVirtual,
  notifyAppOpenProfessionalInvestor,
  virtualApplyState,
} from './add-virtual-applyinfo/store';
import { commonState } from '../../../../stores/common';
import i18n from '../../../../i18n';
import { OpenAccountBootstrapTimeoutError, waitForOpenAccountBootstrap } from '../open-progress/openAccountSession';
import { addAccountBarPadBottom, addAccountContentReserve } from './safeAreaBar';
import { isExcludedFromAddAccountDisplay } from './addAccountDisplayTypes';
import { isDerivativesGranted, refreshUserTradePermission } from '../../trade-products/shared';

type Option = { key: string; label: string; disabled: boolean; type: 'securities' | 'fund' };

const CRYPTO_ACCOUNT = 'CRYPTO_ACCOUNT';

const toastError = (e: unknown) => {
  if (typeof e === 'string') {
    showToast(e);
    return;
  }
  const msg = (e as { message?: string } | null)?.message;
  showToast(msg || i18n.AddAccount.ToastOpFailed);
};

const resolveIsMainland = (status: { isMainlandIdentity?: boolean | string | number | null }, identityType: string) => {
  const flag = status?.isMainlandIdentity;
  if (flag === true || flag === 1 || String(flag).toLowerCase() === 'true') return true;
  if (flag === false || flag === 0 || String(flag).toLowerCase() === 'false') return false;
  return identityType === 'MAINLAND_CHINA';
};

const isPiCertified = (status: ProfessionalInvestorAssessmentStatus) =>
  status?.professionalInvestorCertificationStatus === 'FINISHED'; //如果已通过PI认证，则返回true

const isAggressiveVirtualRisk = (latest: { levelCode?: string | null } | null) =>
  String(latest?.levelCode || '') === 'AGGRESSIVE';

/** 对齐活动页 showDialog：居中实底卡片。不用 Modal 面板，避免 WebView 里背景发透。 */
const openGateModal = (message: string, confirmText: string, onConfirm: () => void) => {
  const dark = commonState.theme === 'dark';
  const cardBg = dark ? '#1B212D' : '#FFFFFF';
  const titleColor = dark ? '#FFFFFF' : '#1B212D';
  const messageColor = dark ? '#C2C3CB' : '#858585';
  const cancelColor = dark ? '#FFFFFF' : '#1B212D';
  const cancelBg = dark ? 'transparent' : '#FFFFFF';

  const title = i18n.OpenProgress.Common.WarmTip || '温馨提示';
  const cancelText = i18n.AddAccount.Cancel;

  let dialog: { close: (destroy?: boolean) => void } | null = null;
  const close = () => dialog?.close();

  dialog = showDialog(() => (
    <div
      style={{
        top: '0',
        right: '0',
        bottom: '0',
        left: '0',
        display: 'flex',
        'align-items': 'center',
        'justify-content': 'center',
        padding: '0 32px',
        'background-color': 'rgba(0, 0, 0, 0.45)',
        'box-sizing': 'border-box',
      }}
      onclick={close}
    >
      <div
        style={{
          width: '100%',
          'max-width': '280px',
          padding: '24px 20px',
          'border-radius': '16px',
          'background-color': cardBg,
          'box-sizing': 'border-box',
        }}
        onclick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            'font-size': '20px',
            'line-height': '28px',
            'font-weight': '700',
            'text-align': 'center',
            color: titleColor,
          }}
        >
          {title}
        </div>
        <div
          style={{
            'margin-top': '8px',
            'font-size': '16px',
            'line-height': '22px',
            'text-align': 'center',
            color: messageColor,
          }}
        >
          {message}
        </div>
        <div style={{ display: 'flex', 'margin-top': '32px' }}>
          <button
            type="button"
            style={{
              flex: '1',
              height: '44px',
              'margin-right': '12px',
              border: '1px solid #858585',
              'border-radius': '22px',
              background: cancelBg,
              color: cancelColor,
              'font-size': '14px',
              'font-weight': '700',
            }}
            onclick={close}
          >
            {cancelText}
          </button>
          <button
            type="button"
            style={{
              flex: '1',
              height: '44px',
              border: '0',
              'border-radius': '22px',
              background: '#1060FF',
              color: '#FFFFFF',
              'font-size': '14px',
              'font-weight': '700',
            }}
            onclick={() => {
              close();
              onConfirm();
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  ));
  dialog.style.zIndex = '2000';
};

const openPiRequiredModal = () => {
  openGateModal(i18n.AddAccount.PiRequiredTip, i18n.AddAccount.GoAuthenticate, () => {
    if ((window as any).ReactNativeWebView) {
      notifyAppOpenProfessionalInvestor();
    } else {
      showToast(i18n.AddAccount.ToastPiInApp);
    }
  });
};

const openRiskRequiredModal = () => {
  openGateModal(i18n.AddAccount.RiskRequiredTip, i18n.AddAccount.GoRiskAssess, () => {
    if ((window as any).ReactNativeWebView) {
      postToApp({
        type: 'WEB_TO_APP_OpenNative',
        payload: { nativePath: '/risk-assessment' },
        timestamp: Date.now(),
      });
    } else {
      showToast(i18n.AddAccount.ToastRiskInApp);
    }
  });
};

const openDerivativesRequiredModal = () => {
  openGateModal(i18n.AddAccount.DerivativesRequiredTip, i18n.AddAccount.ActivateDerivativesNow, () => {
    if ((window as any).ReactNativeWebView) {
      postToApp({
        type: 'WEB_TO_APP_OpenNative',
        payload: { nativePath: '/trade-products?preselect=derivatives' },
        timestamp: Date.now(),
      });
    } else {
      showToast(i18n.AddAccount.ToastDerivativesInApp);
    }
  });
};

const getApplyStatus = (data: any) => data?.auditInfo?.status || data?.status || '';

const SelectTypes = () => {
  /** 深链 ?step=SelectTypes|2 进入时隐藏上一步，避免回到 ApplyList */
  const hidePrev =
    location.query.step === 'SelectTypes' || location.query.step === '2';

  const state = reactive({
    /** 已完成开户申请的证券账户类型（置灰不可选） */
    beforeSelected: [] as string[],
    /** finishedSecuritiesAccountTypes 是否已返回；未返回前全部 checkbox 禁用 */
    finishedTypesReady: false,
    /** 本次新选中的 code（单选） */
    selected: '' as string,
    userId: '',
    identityType: '',
    options: [] as Option[],
    loading: true,
    submitting: false,
    piChecking: false,
    /** 勾选加密货币时校验通过后为 true */
    piCertified: false,
    /** getByUserId 缓存：BACK 续办复用 */
    applyData: null as any,
  });

  /** 从 busConfigs 构建勾选列表（可在字典晚到后再次调用） */
  const rebuildOptionsFromBusConfigs = () => {
    const finishedTypes = state.beforeSelected;
    const allSec = (busConfigs.securitiesAccountType as any[]) || [];
    // 衍生产品 / A股通 已迁至 App「交易品种」激活：加开仅保留账户类别
    state.options = allSec
      .filter((item) => !isExcludedFromAddAccountDisplay(String(item.value || ''), String(item.label || '')))
      .map((item) => ({
        type: 'securities' as const,
        key: item.value,
        label: item.label,
        disabled: finishedTypes.includes(item.value),
      }));
  };

  const load = async () => {
    state.loading = true;
    try {
      // App WebView：等本次 OpenAccount Bootstrap 后再拉鉴权接口
      await waitForOpenAccountBootstrap();
      await initBusConfigs();

      /* 已完成开户类型：单独跟踪返回态，未返回前 checkbox 全部禁用 */
      const finishedTypesPromise = fetchFinishedSecuritiesAccountTypes()
        .then((types) => {
          state.beforeSelected = types;
          state.finishedTypesReady = true;
          // busConfigs 可能已先渲染出列表，需按已开通类型刷新 disabled
          if (state.options.length > 0) {
            rebuildOptionsFromBusConfigs();
          }
          return types;
        })
        .catch((e) => {
          // 失败也视为已响应，避免永久不可点（未开通列表按空处理）
          state.finishedTypesReady = true;
          throw e;
        });

      const [data] = await Promise.all([
        fetchAllInfos('ADD_ACCOUNT') as Promise<any>,
        finishedTypesPromise,
      ]);

      state.applyData = data || null;
      const userBase = data?.userBase || {};

      state.userId = userBase.userId || userBase.mobile || userBase.id || '';
      // 大陆身份以 identityType 为准（BACK 后 idCardType 可能已是境外证件）
      state.identityType = userBase.identityType || '';
      addAccState.fullName = userBase.fullName || '';

      rebuildOptionsFromBusConfigs();

      // BACK 且为加密货币：预选并复用单号（需已通过 PI，否则下一步仍会拦截）
      if (getApplyStatus(data) === 'BACK' && String(data?.securitiesAccountType || '').includes(CRYPTO_ACCOUNT)) {
        state.selected = CRYPTO_ACCOUNT;
        addAccState.busId = String(data?.id || addAccState.busId || '');
        addAccState.securitiesAccountType = CRYPTO_ACCOUNT;
        try {
          const piStatus = await fetchProfessionalInvestorAssessmentStatus();
          state.piCertified = isPiCertified(piStatus);
        } catch {
          state.piCertified = false;
        }
      }
    } catch (e) {
      if (e instanceof OpenAccountBootstrapTimeoutError) {
        showToast(i18n.OpenProgress.Errors.AuthExpired);
      } else {
        toastError(e);
      }
    } finally {
      state.loading = false;
    }
  };

  /** 切语言后字典已刷新，同步已缓存选项的 label */
  const syncOptionLabels = () => {
    const secMap = busConfigsMaps.securitiesAccountType as Record<string, string>;
    state.options.forEach((opt) => {
      if (secMap[opt.key]) opt.label = secMap[opt.key];
    });
  };

  watch(() => commonState.langauge, () => {
    syncOptionLabels();
  });

  /** Theme / 接口字典晚于首屏 load：有证券账户类型且列表仍空时重建勾选 */
  watch(
    () => busConfigsTick.v,
    () => {
      const secLen = (busConfigs.securitiesAccountType as any[])?.length ?? 0;
      if (secLen > 0 && state.options.length === 0) {
        rebuildOptionsFromBusConfigs();
      }
    }
  );

  load();

  /**
   * 加密货币加开的最高优先条件。快照已开通则直接过；
   * 未开通先刷新 /user/info（从激活页返回后 WebView 仍是旧快照），仍未开通则弹窗。
   */
  const ensureDerivativesActivated = async () => {
    if (!isDerivativesGranted()) {
      await refreshUserTradePermission();
    }
    if (isDerivativesGranted()) return true;
    state.selected = '';
    state.piCertified = false;
    openDerivativesRequiredModal();
    return false;
  };

  const select = async (key: string) => {
    if (!state.finishedTypesReady) return;
    const opt = state.options.find((o) => o.key === key);
    if (!opt || opt.disabled) return;

    /* 取消勾选 */
    if (state.selected === key) {
      state.selected = '';
      if (key === CRYPTO_ACCOUNT) state.piCertified = false;
      return;
    }

    if (key === CRYPTO_ACCOUNT) {
      if (state.piChecking) return;
      state.piChecking = true;
      try {
        const derivativesOk = await ensureDerivativesActivated();
        if (!derivativesOk) return;
        const latest = await fetchVirtualAssetRiskLatest();
        if (!isAggressiveVirtualRisk(latest)) {
          state.selected = '';
          state.piCertified = false;
          openRiskRequiredModal();
          return;
        }
        const status = await fetchProfessionalInvestorAssessmentStatus();
        if (!isPiCertified(status)) {
          state.selected = '';
          state.piCertified = false;
          openPiRequiredModal();
          return;
        }
        state.piCertified = true;
        state.selected = key;
      } catch (e) {
        state.selected = '';
        state.piCertified = false;
        toastError(e);
      } finally {
        state.piChecking = false;
      }
      return;
    }

    state.piCertified = false;
    state.selected = key;
  };

  const handleNext = async () => {
    if (!state.finishedTypesReady || state.submitting || state.piChecking) return;

    /** 衍生产品权限已迁 App「交易品种」，加开不再提交 needDerivativeTradePermission */
    if (!state.selected) {
      showToast(i18n.AddAccount.ToastSelectAccountOrPermission);
      return;
    }

    const selectedOpt = state.options.find((o) => o.key === state.selected);
    addAccState.securitiesAccountType = selectedOpt?.type === 'securities' ? state.selected : '';
    addAccState.fundAccountType = selectedOpt?.type === 'fund' ? state.selected : '';
    addAccState.needDerivativeTradePermission = false;

    /* 非虚拟资产（含仅勾交易权限）：保持原加开流程 */
    if (state.selected !== CRYPTO_ACCOUNT) {
      // 经选类型进入协议：上一步应回选类型，勿带 BACK 直跳标记
      addAccState.riskResumeFromList = false;
      nextProgress();
      return;
    }

    state.piChecking = true;
    try {
      const derivativesOk = await ensureDerivativesActivated();
      if (!derivativesOk) return;
    } catch (e) {
      state.selected = '';
      state.piCertified = false;
      toastError(e);
      return;
    } finally {
      state.piChecking = false;
    }

    if (!state.piCertified) {
      openPiRequiredModal();
      return;
    }

    state.submitting = true;
    try {
      const status = await fetchProfessionalInvestorAssessmentStatus();
      if (!isPiCertified(status)) {
        state.piCertified = false;
        state.selected = '';
        openPiRequiredModal();
        return;
      }

      const isMainland = resolveIsMainland(status, state.identityType);
      const cached = state.applyData;
      const isBack = getApplyStatus(cached) === 'BACK';
      const backIsCrypto = String(cached?.securitiesAccountType || '').includes(CRYPTO_ACCOUNT);

      let busId = '';
      if (isBack && backIsCrypto && cached?.id) {
        // BACK：复用原单，回填已填资料，不开新单
        busId = String(cached.id);
        addAccState.busId = busId;
        if (isMainland) {
          await hydrateVirtualApplyFromApplyData(cached);
          addAccState.virtualFlow = true;
          return;
        }
      } else {
        const apply = await saveApply({
          applyType: 'ADD_ACCOUNT',
          securitiesAccountType: CRYPTO_ACCOUNT,
          fundAccountType: '',
          needDerivativeTradePermission: addAccState.needDerivativeTradePermission,
        });
        busId = apply?.id || '';
        addAccState.busId = busId;
      }

      if (isMainland) {
        virtualApplyState.busId = busId;
        // 同页切换，避免懒加载路由 + Suspense fallback=null 造成白屏
        addAccState.virtualFlow = true;
        return;
      }

      notifyAppContinueAddVirtual({
        busId,
        isMainlandIdentity: false,
      });

      // 非 App WebView：原生不会接管页面，给明确提示
      if (!(window as any).ReactNativeWebView) {
        showToast(i18n.AddAccount.ToastVirtualInApp);
      }
    } catch (e) {
      toastError(e);
    } finally {
      state.submitting = false;
    }
  };

  return (
    <div>
      {/* ── 主内容区（左右内边距与上一页 ApplyList 的 padding 对齐） ── */}
      <div class="padding" style={{ 'margin-bottom': `${addAccountContentReserve(140)}px` }}>
        {/* 标题 */}
        {/* <div class="font-s margin-b font-bold color-D">加开账户(个人)</div> */}

        {/* 用户 ID 卡片 */}
        {/* <div class="bg-c-XD round padding-x padding-y-L margin-b flex justify-space-between align-center">
          <span class="font-s color-D font-bold">{i18n.AddAccount.ClientId}</span>
          <span class="font-s-S color-D font-bold">{state.userId}</span>
        </div> */}

        {/* 新增账户类别 */}
        <div class="flex flex-column gap-12px margin-b">
          <div class="color-D">{i18n.AddAccount.NewAccountCategory}</div>

          <div class="flex flex-column gap-12px">
            <For each={state.options}>
              {(opt) => {
                const isCryptoChecking = opt.key === CRYPTO_ACCOUNT && state.piChecking;
                const itemDisabled = !state.finishedTypesReady || opt.disabled || isCryptoChecking;
                return (
                  <DiFiCheckBox
                    type="checkbox"
                    value={opt.key}
                    label={isCryptoChecking ? undefined : opt.label}
                    labelRender={
                      isCryptoChecking
                        ? () => (
                            <span class="flex align-center gap-8px">
                              <span>{opt.label}</span>
                              <DiFiLoading overlay={false} size="sm" />
                            </span>
                          )
                        : undefined
                    }
                    checked={opt.disabled || state.selected === opt.key}
                    disabled={itemDisabled}
                    class={`font-s-S ${itemDisabled ? 'color-XL' : 'color-D'}`}
                    onChange={() => {
                      void select(opt.key);
                    }}
                  />
                );
              }}
            </For>
          </div>
        </div>
      </div>

      {/* ── 下一步按钮（水平内边距与 ApplyList 底部栏一致） ── */}
      <div
        class="fixed position-b position-l width-100 bg-c flex gap-8px"
        style={{
          'padding-top': '16px',
          'padding-left': '16px',
          'padding-right': '16px',
          'padding-bottom': `${addAccountBarPadBottom(16)}px`,
          'z-index': 150,
        }}
      >
        <If when={!hidePrev}>
          <button
            class="default-btn btn-rounded"
            style={{ flex: 1 }}
            onclick={() => {
              lastProgress();
            }}
            disabled={state.submitting}
          >
            {i18n.OpenProgress.Common.Prev}
          </button>
        </If>
        <button
          class="confirm-btn btn-rounded"
          style={{
            flex: hidePrev ? 1 : 2,
          }}
          onclick={() => {
            void handleNext();
          }}
          disabled={state.submitting || state.piChecking}
        >
          {state.submitting ? i18n.AddAccount.Processing : i18n.OpenProgress.Common.Next}
        </button>
      </div>
    </div>
  );
};

export default SelectTypes;
